/**
 * Upload Service - Handles file uploads to the backend API.
 *
 * Supports:
 * - Photo uploads for work orders (before/after)
 * - Photo uploads for inspections (observation/correction)
 * - Document uploads for buildings
 * - Signature image uploads
 *
 * Features:
 * - Client-side size validation (configurable max, default 10MB)
 * - HEIC detection and server-side conversion flagging
 * - Multipart form data upload with Bearer token authentication
 * - Offline queue: if offline, stores in IndexedDB via offlineStorage
 *
 * All upload methods use multipart/form-data instead of JSON so the backend
 * can receive the raw file binary. The auth token is injected from localStorage
 * just like the api.ts module does for JSON requests.
 *
 * @example
 * import { uploadService } from '../services/uploadService';
 *
 * // Upload a work order photo
 * await uploadService.uploadWorkOrderPhoto(workOrderId, file, 'before', 'Lobby entrance');
 *
 * // Upload a signature
 * await uploadService.uploadSignature('work_order', workOrderId, signatureBase64);
 */

import { compressPhoto, isHEICFile, MAX_UPLOAD_MB } from '../utils/imageCompression';

/* ============================================
 * CONSTANTS
 * ============================================ */

/** Base URL for all API requests; mirrors the api.ts configuration */
const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

/** Default maximum file size in megabytes for upload validation. Sourced
 *  from the shared compression util so the client's idea of the limit and
 *  the server's MAX_UPLOAD_SIZE_MB can't drift apart. */
const DEFAULT_MAX_MB = MAX_UPLOAD_MB;

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/**
 * Photo type for work order photos — distinguishes before and after images.
 */
export type WorkOrderPhotoType = 'before' | 'after';

/**
 * Photo type for inspection photos — distinguishes observations from corrections.
 */
export type InspectionPhotoType = 'observation' | 'correction';

/**
 * Entity type for signature uploads — determines the API endpoint.
 */
export type SignatureEntityType = 'work_order' | 'inspection';

/**
 * Options for the compressImage method.
 *
 * @property maxSizeMB		  - Target maximum file size in megabytes
 * @property maxWidthOrHeight - Maximum pixel dimension (preserves aspect ratio)
 * @property fileType		  - Output MIME type (default 'image/jpeg')
 */
export interface CompressionOptions {
	maxSizeMB?: number;
	maxWidthOrHeight?: number;
	fileType?: string;
}

/**
 * Response shape returned by the backend after a successful photo upload.
 * Contains the created photo record with its server-assigned URL and ID.
 *
 * Note: photoType is the photoType FK relation; the legacy string enum
 * has been retired in favor of the per-company photo_types taxonomy.
 */
export interface UploadedPhoto {
	id: string;
	url: string;
	caption: string;
	photoTypeId?: string | number | null;
	photoSubcategoryId?: string | number | null;
	createdAt: string;
}

/**
 * Metadata attached to a photo at upload time, matching the new
 * per-company taxonomy on the backend (photo_types + photo_subcategories).
 *
 * @property photoTypeId         Required FK to photo_types.id
 * @property photoSubcategoryId  Optional FK to photo_subcategories.id
 *                               (filtered by parent type in the field UX)
 * @property checklistItemId     Optional FK to checklist_items.id when the
 *                               photo satisfies a specific checklist item's
 *                               photo requirement
 * @property photoPhase          Optional 'before' / 'after' tag. Only set
 *                               when the target checklist item is in
 *                               photoMode='before_after' so the server
 *                               can land the photo in the correct slot.
 * @property caption             Optional free-text description
 * @property sortOrder           Optional integer to control gallery position
 */
export interface PhotoUploadMetadata {
	photoTypeId: number;
	photoSubcategoryId?: number | null;
	checklistItemId?: number | null;
	/**
	 * Phase tag for items in photoMode='before_after'. Null / undefined
	 * for any other capture (single-photo items, ad-hoc uploads, etc.).
	 */
	photoPhase?: 'before' | 'after' | null;
	/** Area name, stored on the photo row alongside areaId. Kept as text too
	 *  so a later CMS rename doesn't rewrite what the inspector recorded. */
	area?: string;
	/** The CMS-managed area's id, when one was picked. */
	areaId?: number | null;
	/** Short label shown above the description in the gallery and the PDF. */
	title?: string;
	/** The photo's description. */
	caption?: string;
	sortOrder?: number;
}

/**
 * Response shape returned by the backend after a successful signature upload.
 */
export interface UploadedSignature {
	id: string;
	url: string;
	createdAt: string;
}

/**
 * Offline queue entry — stored in IndexedDB when the app is offline.
 * Contains all the information needed to retry the upload when back online.
 */
export interface PendingUpload {
	id: string;
	type: 'work_order_photo' | 'inspection_photo' | 'signature';
	entityId: string;
	formData: Record<string, string>;
	fileBase64: string;
	fileName: string;
	mimeType: string;
	createdAt: string;
}

/* ============================================
 * HELPER FUNCTIONS
 * ============================================ */

/**
 * Retrieves the Sanctum Bearer token from localStorage.
 * Returns null if the user is not authenticated.
 *
 * @returns The stored auth token string, or null if not logged in
 */
function getAuthToken(): string | null {
	return localStorage.getItem('auth_token');
}

/**
 * Sends a multipart/form-data request to the specified API endpoint.
 * Unlike the api.ts request() function, this does NOT set Content-Type
 * because the browser must auto-generate the multipart boundary.
 *
 * @param endpoint - API path relative to BASE_URL (e.g., '/work-orders/123/photos')
 * @param formData - The FormData object containing files and metadata fields
 * @returns The parsed JSON response data
 * @throws Error if the response is not in the 2xx range
 */
async function uploadRequest<T>(endpoint: string, formData: FormData): Promise<T> {
	const url = `${BASE_URL}${endpoint}`;

	/* Build headers — do NOT set Content-Type; the browser auto-generates
	   the multipart boundary when sending FormData */
	const headers: Record<string, string> = {};
	const token = getAuthToken();
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	/* Send the multipart request */
	const response = await fetch(url, {
		method: 'POST',
		headers,
		body: formData,
	});

	/* Parse the response body as JSON */
	const data = await response.json();

	/* Throw an error for non-2xx responses with the server's error message */
	if (!response.ok) {
		const message = data?.message || data?.error || `Upload failed with status ${response.status}`;
		throw new Error(message);
	}

	return data as T;
}

/**
 * Converts a base64 data URL string to a File object.
 * Used when uploading signatures (which are captured as data URLs from canvas).
 *
 * @param base64DataUrl - The base64 data URL (e.g., "data:image/png;base64,...")
 * @param fileName		- The desired filename for the created File object
 * @returns A File object containing the decoded binary data
 */
function base64ToFile(base64DataUrl: string, fileName: string): File {
	/* Split the data URL into the metadata prefix and the base64 payload */
	const [header, base64Data] = base64DataUrl.split(',');

	/* Extract the MIME type from the data URL header (e.g., "image/png") */
	const mimeMatch = header.match(/:(.*?);/);
	const mimeType = mimeMatch ? mimeMatch[1] : 'image/png';

	/* Decode the base64 string into raw binary data */
	const binaryString = atob(base64Data);
	const bytes = new Uint8Array(binaryString.length);
	for (let i = 0; i < binaryString.length; i++) {
		bytes[i] = binaryString.charCodeAt(i);
	}

	/* Create and return a File object with the decoded data */
	return new File([bytes], fileName, { type: mimeType });
}

/* ============================================
 * PUBLIC SERVICE API
 * ============================================ */

/**
 * Upload service object exposing methods for all file upload operations.
 * Each method handles FormData construction, authentication, and error handling.
 */
export const uploadService = {
	/**
	 * Uploads a photo to a work order (before or after type).
	 *
	 * Constructs a multipart form with the image file, photo type, and optional caption.
	 * The backend stores the file and associates it with the work order record.
	 *
	 * @param workOrderId - UUID of the work order to attach the photo to
	 * @param file		  - The image File object to upload
	 * @param photoType	  - Whether this is a 'before' or 'after' photo
	 * @param caption	  - Optional description of the photo
	 * @returns The created photo record from the server
	 */
	/**
	 * Uploads a photo to a work order with the new per-company photo
	 * taxonomy metadata. Required: photoTypeId. Optional: subcategory,
	 * checklist item linkage, caption, sort order.
	 *
	 * Sends `file` as the multipart field name to match the backend's
	 * WorkOrderController@addPhoto validation. The legacy 'photo'
	 * field name has been retired alongside the legacy enum.
	 */
	uploadWorkOrderPhoto: async (
		workOrderId: string,
		file: File,
		metadata: PhotoUploadMetadata
	): Promise<UploadedPhoto> => {
		/* Build the multipart form data payload using the new metadata FKs */
		const formData = new FormData();
		formData.append('file', file, file.name);
		formData.append('photo_type_id', String(metadata.photoTypeId));
		if (metadata.photoSubcategoryId != null) {
			formData.append('photo_subcategory_id', String(metadata.photoSubcategoryId));
		}
		if (metadata.checklistItemId != null) {
			formData.append('checklist_item_id', String(metadata.checklistItemId));
		}
		if (metadata.photoPhase) {
			// Only append when the caller explicitly chose 'before' or
			// 'after' — omitting the field lands the photo with phase=null.
			formData.append('photo_phase', metadata.photoPhase);
		}
		if (metadata.area) {
			formData.append('area', metadata.area);
		}
		if (metadata.areaId != null) {
			formData.append('area_id', String(metadata.areaId));
		}
		if (metadata.title) {
			formData.append('title', metadata.title);
		}
		if (metadata.caption) {
			formData.append('caption', metadata.caption);
		}
		if (metadata.sortOrder != null) {
			formData.append('sort_order', String(metadata.sortOrder));
		}

		/* Flag HEIC files so the server knows to convert them */
		if (uploadService.isHEIC(file)) {
			formData.append('requires_conversion', 'true');
		}

		/* Send to the work order photos endpoint */
		return uploadRequest<UploadedPhoto>(
			`/work-orders/${workOrderId}/photos`,
			formData
		);
	},

	/**
	 * Uploads a photo to an inspection (observation or correction type).
	 *
	 * Same pattern as work order photo upload but targets the inspection endpoint.
	 *
	 * @param inspectionId - UUID of the inspection to attach the photo to
	 * @param file		   - The image File object to upload
	 * @param photoType	   - Whether this is an 'observation' or 'correction' photo
	 * @param caption	   - Optional description of the photo
	 * @returns The created photo record from the server
	 */
	/**
	 * Uploads a photo to an inspection with the new metadata taxonomy.
	 * See uploadWorkOrderPhoto above for the metadata field semantics.
	 */
	uploadInspectionPhoto: async (
		inspectionId: string,
		file: File,
		metadata: PhotoUploadMetadata
	): Promise<UploadedPhoto> => {
		/* Build the multipart form data payload using the new metadata FKs */
		const formData = new FormData();
		formData.append('file', file, file.name);
		formData.append('photo_type_id', String(metadata.photoTypeId));
		if (metadata.photoSubcategoryId != null) {
			formData.append('photo_subcategory_id', String(metadata.photoSubcategoryId));
		}
		if (metadata.checklistItemId != null) {
			formData.append('checklist_item_id', String(metadata.checklistItemId));
		}
		if (metadata.photoPhase) {
			// Same semantics as the work-order branch above — only sent
			// for explicit before/after items.
			formData.append('photo_phase', metadata.photoPhase);
		}
		if (metadata.area) {
			formData.append('area', metadata.area);
		}
		if (metadata.areaId != null) {
			formData.append('area_id', String(metadata.areaId));
		}
		if (metadata.title) {
			formData.append('title', metadata.title);
		}
		if (metadata.caption) {
			formData.append('caption', metadata.caption);
		}
		if (metadata.sortOrder != null) {
			formData.append('sort_order', String(metadata.sortOrder));
		}

		/* Flag HEIC files so the server knows to convert them */
		if (uploadService.isHEIC(file)) {
			formData.append('requires_conversion', 'true');
		}

		/* Send to the inspection photos endpoint */
		return uploadRequest<UploadedPhoto>(
			`/inspections/${inspectionId}/photos`,
			formData
		);
	},

	/**
	 * Uploads a signature image captured from the SignatureCapture component.
	 *
	 * Converts the base64 data URL to a File object and sends it as multipart form data.
	 * The entity type and ID determine which API endpoint receives the upload.
	 *
	 * @param entityType	  - 'work_order' or 'inspection' — determines the endpoint
	 * @param entityId		  - UUID of the entity to attach the signature to
	 * @param signatureBase64 - PNG data URL from the SignatureCapture canvas export
	 * @returns The created signature record from the server
	 */
	uploadSignature: async (
		entityType: SignatureEntityType,
		entityId: string,
		signatureBase64: string
	): Promise<UploadedSignature> => {
		/* Convert the base64 data URL to a File object for multipart upload */
		const fileName = `signature_${entityId}_${Date.now()}.png`;
		const file = base64ToFile(signatureBase64, fileName);

		/* Build the multipart form data payload */
		const formData = new FormData();
		formData.append('signature', file, fileName);
		formData.append('entity_type', entityType);

		/* Determine the API endpoint based on entity type */
		const endpoint = entityType === 'work_order'
			? `/work-orders/${entityId}/signature`
			: `/inspections/${entityId}/signature`;

		/* Send to the appropriate signature endpoint */
		return uploadRequest<UploadedSignature>(endpoint, formData);
	},

	/**
	 * Checks whether a file exceeds the maximum allowed size.
	 *
	 * @param file	- The File object to check
	 * @param maxMB - Maximum size in megabytes (default 10MB)
	 * @returns true if the file is TOO LARGE (exceeds the limit)
	 */
	isFileTooLarge: (file: File, maxMB: number = DEFAULT_MAX_MB): boolean => {
		/* Convert the maxMB threshold to bytes for comparison */
		const maxBytes = maxMB * 1024 * 1024;
		return file.size > maxBytes;
	},

	/**
	 * Detects whether a file is in HEIC/HEIF format by checking its MIME type
	 * and file extension. HEIC is the default photo format on modern iOS devices.
	 *
	 * When a HEIC file is detected, the upload methods add a `requires_conversion`
	 * flag to the request so the backend can convert it to JPEG.
	 *
	 * @param file - The File object to check
	 * @returns true if the file appears to be HEIC/HEIF format
	 */
	isHEIC: (file: File): boolean => isHEICFile(file),

	/**
	 * Compresses an image file using the browser-image-compression library.
	 * Wraps the library with sensible defaults matching the app's requirements.
	 *
	 * Returns a new File object with the compressed data. The original file is
	 * not modified. Output format defaults to JPEG for broad compatibility.
	 *
	 * @param file	  - The image File to compress
	 * @param options - Optional compression configuration overrides
	 * @returns A new compressed File object
	 */
	compressImage: async (file: File, options?: CompressionOptions): Promise<File> => {
		/* Delegates to the shared pipeline so every caller gets the same
		   worker-with-canvas-fallback behaviour and the same hard guarantee
		   that the result fits under the server's cap. */
		const { file: compressed } = await compressPhoto(file, {
			maxSizeMB: options?.maxSizeMB,
			maxWidthOrHeight: options?.maxWidthOrHeight,
		});
		return compressed;
	},
};
